import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { SortField } from '~/utils/types/common';
import { DEFAULT_PAGE, ROWS_PER_PAGE } from '~/utils/constants/common';
import { ICRMBoard, ICustomerFile, ICustomerList, ISelectCustomer } from '~/utils/interface/customer';
import {
  handleGetContacts,
  handleGetCustomerFiles,
  handleGetCustomers,
  handleGetSelectionCustomers,
  handleGetCustomerFolders,
  handleGetCRMBoard,
  handleGetFiles,
} from './customerThunk';
import { INote } from '~/utils/interface/note';
import { handleGetNoteList } from '../job/jobThunk';
import { IFolder } from '~/utils/interface/folder';

export interface CustomerState {
  selectCustomers: Array<ICustomerList>;
  filter: IFilter;
  filterBoard: IFilterBoard;
  currentPage: number;
  totalPages: number;
  needRefreshData: boolean;
  customers: Array<ICustomerList>;
  contacts: Array<ICustomerList>;
  selectedIds: Array<string>;
  customerId: string;
  openAlertDelete: boolean;
  openAlertDeleteContact: boolean;
  openAlertDeleteFolder: boolean;
  menuSelectedId: string | undefined;
  notesList: Array<INote>;
  customerFiles: Array<ICustomerFile>;
  filterFile: ICustomerFileFilter;
  folders: Array<IFolder>;
  filterFolder: IFilterFolder;
  menuSelectedFolderId: string | undefined;
  selectedFolderIds: Array<string>;
  selectionCustomers: Array<ISelectCustomer>;
  CRMBoard: Array<ICRMBoard>;
  files: Array<ICustomerFile>;
}
interface IFilterBoard {
  search?: string;
  searchFields?: Array<string>;
}
interface IFilterFolder {
  page?: number;
  limit?: number;
  type?: string;
}

interface IFilter {
  page?: number;
  limit?: number;
  search?: string;
  searchFields?: Array<string>;
  sort?: Array<SortField>;
}
interface ICustomerFileFilter {
  page?: number;
  limit?: number;
  selectedTags?: Array<string>;
}
const initialState: CustomerState = {
  CRMBoard: [],
  files: [],
  selectCustomers: [],
  openAlertDeleteContact: false,
  selectionCustomers: [],
  currentPage: DEFAULT_PAGE,
  totalPages: 0,
  needRefreshData: false,
  filter: {
    page: 1,
    limit: ROWS_PER_PAGE,
    search: '',
    searchFields: [],
    sort: [],
  },
  filterBoard: {
    search: '',
    searchFields: ['name'],
  },
  selectedIds: [],
  customers: [],
  contacts: [],
  customerId: '',
  openAlertDelete: false,
  openAlertDeleteFolder: false,
  menuSelectedId: undefined,
  notesList: [],
  customerFiles: [],
  filterFile: {
    page: 1,
    limit: ROWS_PER_PAGE,
  },
  folders: [],
  filterFolder: {
    type: 'customers',
  },
  menuSelectedFolderId: undefined,
  selectedFolderIds: [],
};

const customerSlice = createSlice({
  name: 'customer',
  initialState,
  reducers: {
    changeNeedRefreshData(state, action: PayloadAction<boolean>) {
      state.needRefreshData = action.payload;
    },
    setFilter(state, action: PayloadAction<IFilter>) {
      state.filter = { ...state.filter, ...action.payload };
    },
    setFilterFile(state, action: PayloadAction<ICustomerFileFilter>) {
      state.filterFile = { ...state.filterFile, ...action.payload };
    },
    setFilterFolder(state, action: PayloadAction<IFilterFolder>) {
      state.filterFolder = { ...state.filterFolder, ...action.payload };
    },
    changeSelectedIds(state, action: PayloadAction<Array<string>>) {
      state.selectedIds = [...action.payload];
    },
    changeSelectedFolderIds(state, action: PayloadAction<Array<string>>) {
      state.selectedFolderIds = [...action.payload];
    },
    changeCustomerId(state, action: PayloadAction<string>) {
      state.customerId = action.payload;
    },
    changeOpenAlertDelete(state, action: PayloadAction<boolean>) {
      state.openAlertDelete = action.payload;
      if (!state.openAlertDelete) {
        state.menuSelectedId = undefined;
      }
    },

    changeOpenAlertDeleteFolder(state, action: PayloadAction<boolean>) {
      state.openAlertDeleteFolder = action.payload;
    },
    changeMenuSelectId(state, action: PayloadAction<string | undefined>) {
      state.menuSelectedId = action.payload;
    },
    changeMenuSelectFolderId(state, action: PayloadAction<string | undefined>) {
      state.menuSelectedFolderId = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(handleGetCustomers.pending, (_state, _action) => {})

      .addCase(handleGetCustomers.fulfilled, (state, action) => {
        state.customers = [...action.payload.responses];
        state.currentPage = action.payload.pagination.page;
        state.totalPages = action.payload.pagination.totalPages;
        state.needRefreshData = false;
      })

      .addCase(handleGetCustomers.rejected, (_state, _action) => {})

      .addCase(handleGetContacts.pending, (_state, _action) => {})

      .addCase(handleGetContacts.fulfilled, (state, action) => {
        state.contacts = [...action.payload.responses];
        state.currentPage = action.payload.pagination.page;
        state.totalPages = action.payload.pagination.totalPages;
        state.needRefreshData = false;
      })
      .addCase(handleGetCRMBoard.fulfilled, (state, action) => {
        state.CRMBoard = [
          ...action.payload.responses.map((item) => ({
            ...item,
            filter: {
              page: 1,
              crmStatusId: item.id,
              sort: [{ field: 'crmPosition', type: 'ASC' }],
            },
          })),
        ];
        state.needRefreshData = false;
      })
      .addCase(handleGetContacts.rejected, (_state, _action) => {})
      .addCase(handleGetSelectionCustomers.pending, (_state, _action) => {})

      .addCase(handleGetSelectionCustomers.fulfilled, (state, action) => {
        state.selectionCustomers = [...action.payload.responses];

        state.needRefreshData = false;
      })

      .addCase(handleGetSelectionCustomers.rejected, (_state, _action) => {})

      .addCase(handleGetNoteList.fulfilled, (state, action) => {
        state.notesList = [...action.payload.responses];
        state.needRefreshData = false;
      })
      .addCase(handleGetCustomerFiles.fulfilled, (state, action) => {
        state.customerFiles = [...action.payload.responses];
      })
      .addCase(handleGetCustomerFolders.fulfilled, (state, action) => {
        state.folders = [...(action.payload.responses || action.payload)];
        state.needRefreshData = false;
      })

      .addCase(handleGetFiles.fulfilled, (state, action) => {
        state.files = [...action.payload.responses];

        state.currentPage = action.payload.pagination.page;

        state.totalPages = action.payload.pagination.totalPages;
        state.needRefreshData = false;
      });
  },
});

export const customerActions = customerSlice.actions;

const customerReducer = customerSlice.reducer;
export default customerReducer;
